import type { EmUnit } from '../types'

/**
 * Gets the width of an element in em units and the font size in em units
 * @param element - The HTML element to measure
 * @returns An object containing the width and fontSize in em units
 * @throws Error if the element's computed styles cannot be parsed
 */
export function getElementSizeInEm(element: HTMLElement): {
  width: EmUnit
  fontSize: EmUnit
} {
  const computedStyle = window.getComputedStyle(element)
  const width = computedStyle.width
  const fontSize = computedStyle.fontSize

  // Convert width to em units
  const widthValue = parseFloat(width)
  const fontSizeValue = parseFloat(fontSize)

  if (isNaN(widthValue) || isNaN(fontSizeValue)) {
    throw new Error('Unable to parse element dimensions')
  }

  return {
    width: `${widthValue / fontSizeValue}em`,
    fontSize: fontSize as EmUnit,
  }
}

/**
 * A WeakMap that stores target widths (in em units) for digit elements during animations.
 *
 * This map is used as part of the FLIP (First, Last, Invert, Play) animation technique
 * to ensure smooth transitions between numbers of different widths (e.g., 1 → 10).
 *
 * Using WeakMap ensures that:
 * 1. Memory is properly cleaned up when elements are removed from the DOM
 * 2. No memory leaks occur during rapid number changes
 * 3. Garbage collection can still occur for removed elements
 *
 * The map is populated during the animation lifecycle:
 * 1. When a number changes, the new width is measured using getElementSizeInEm
 * 2. The width is stored here as a temporary reference
 * 3. The NumberSection component uses these stored widths to animate between states
 */
export const targetWidths = new WeakMap<HTMLElement, EmUnit>()
